import { useCallback, useState } from 'react';

export type ToastType = 'info' | 'success' | 'warning' | 'error';

export interface ToastLinkedBody {
  text: string;
  href: string;
  linkLabel: string;
}

export type ToastBody = string | ToastLinkedBody;

export interface ToastOptions {
  // helpSlug, when set on an error toast, renders a "Troubleshoot ->" link that
  // opens the contextual help drawer scrolled to the named article.
  helpSlug?: string;
}

export interface ToastMessage {
  id: number;
  type: ToastType;
  message: ToastBody;
  helpSlug?: string;
}

interface ToastApi {
  messages: ToastMessage[];
  push: (message: ToastBody, type?: ToastType, options?: ToastOptions) => void;
  error: (message: ToastBody, options?: ToastOptions) => void;
  success: (message: ToastBody) => void;
  info: (message: ToastBody) => void;
  warning: (message: ToastBody) => void;
  dismiss: (id: number) => void;
}

let counter = 0;

const MAX_VISIBLE_TOASTS = 4;

export function useToast(): ToastApi {
  const [messages, setMessages] = useState<ToastMessage[]>([]);

  const push = useCallback(
    (message: ToastBody, type: ToastType = 'info', options?: ToastOptions) => {
      counter += 1;
      const id = counter;
      setMessages((m) => {
        const next = [...m, { id, message, type, helpSlug: options?.helpSlug }];
        while (next.length > MAX_VISIBLE_TOASTS) next.shift();
        return next;
      });
      setTimeout(() => {
        setMessages((m) => m.filter((t) => t.id !== id));
      }, 5000);
    },
    [],
  );

  const error = useCallback(
    (message: ToastBody, options?: ToastOptions) => push(message, 'error', options),
    [push],
  );
  const success = useCallback((message: ToastBody) => push(message, 'success'), [push]);
  const info = useCallback((message: ToastBody) => push(message, 'info'), [push]);
  const warning = useCallback((message: ToastBody) => push(message, 'warning'), [push]);

  const dismiss = useCallback((id: number) => {
    setMessages((m) => m.filter((t) => t.id !== id));
  }, []);

  return { messages, push, error, success, info, warning, dismiss };
}
